import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingShell from '../marketing/MarketingShell';
import ProductShell from '../marketing/ProductShell';
import PageMeta from '../seo/PageMeta';

const API_URL = process.env.REACT_APP_API_URL || '';

export const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus('Message sent. We will get back to you soon.');
        setForm({ name: '', email: '', message: '' });
      } else {
        setStatus(data.error || 'Could not send message.');
      }
    } catch {
      setStatus('Network error. Check that the API is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProductShell
      title="Contact us"
      eyebrow="Product · Contact"
      lead="Questions about teams, enterprise, or the Pro plan? Send us a message — we typically reply within one business day."
      ctaLabel="Sign in to your account"
      ctaTo="/login"
    >
      <PageMeta />
      <div className="product-contact-panel">
      <form onSubmit={submit} className="contact-form product-contact-form">
        <label>Name</label>
        <input className="contact-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <label>Email</label>
        <input className="contact-input" required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        <label>Message</label>
        <textarea className="contact-input" required rows={5} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
        <button type="submit" className="marketing-btn marketing-btn-primary" disabled={loading} style={{ marginTop: 12 }}>
          {loading ? 'Sending…' : 'Send message'}
        </button>
        {status && <p role="status" style={{ marginTop: 12 }}>{status}</p>}
      </form>
      <p style={{ marginTop: 24 }}>Or email <a href="mailto:support@qnaread.com">support@qnaread.com</a></p>
      </div>
    </ProductShell>
  );
};

export const ForStudentsPage = () => (
  <MarketingShell title="For students">
    <PageMeta />
    <p className="marketing-lead">Structured Q&A, revision, and AI-assisted understanding for your syllabus.</p>
    <div className="marketing-grid">
      <div className="marketing-card"><h3>Organized by topic</h3><p>Filter by notebook, topic, and level.</p></div>
      <div className="marketing-card"><h3>Smart revision</h3><p>Practice and review queues keep you on schedule.</p></div>
      <div className="marketing-card"><h3>AI help</h3><p>Explain tough answers without leaving the app.</p></div>
    </div>
    <Link to="/login" className="marketing-btn marketing-btn-primary">Get started</Link>
  </MarketingShell>
);

export const ForDevelopersPage = () => (
  <MarketingShell title="For developers">
    <PageMeta />
    <p className="marketing-lead">Search, save, and drill technical Q&A with code-friendly formatting.</p>
    <div className="marketing-grid">
      <div className="marketing-card"><h3>Code-aware UI</h3><p>Answers render with syntax highlighting.</p></div>
      <div className="marketing-card"><h3>Deep explains</h3><p>AI breakdowns for interview depth.</p></div>
      <div className="marketing-card"><h3>Track gaps</h3><p>Dashboard shows weak topics from your history.</p></div>
    </div>
    <Link to="/login" className="marketing-btn marketing-btn-primary">Start learning</Link>
  </MarketingShell>
);

export const AiMarketingPage = () => (
  <MarketingShell title="AI assistant">
    <PageMeta />
    <p className="marketing-lead">Explain, suggest improvements, and discover related topics — with daily fair-use limits on Free.</p>
    <ul className="marketing-list">
      <li>In-depth Markdown explanations with diagrams</li>
      <li>Memory-map style recaps</li>
      <li>Related topic suggestions</li>
    </ul>
    <Link to="/login" className="marketing-btn marketing-btn-primary">Try QnaRead</Link>
  </MarketingShell>
);

export const SecurityMarketingPage = () => (
  <ProductShell
    title="Security & trust"
    eyebrow="Product · Security"
    lead="Defense in depth for learners and teams: encrypted transport, HttpOnly cookies, optional MFA, and Stripe for billing."
    ctaLabel="Sign in securely"
    ctaTo="/login"
  >
    <PageMeta />
    <div className="product-security-grid">
    <ul className="pricing-features product-security-list">
      <li>JWT access tokens + rotating refresh tokens in HttpOnly cookies</li>
      <li>Role-based access (USER vs ADMIN) enforced on every API route</li>
      <li>Optional authenticator-app MFA (TOTP) at sign-in</li>
      <li>Google sign-in when configured — verified server-side</li>
      <li>Stripe Checkout for billing — card data never touches our servers</li>
      <li>Security response headers (X-Frame-Options, nosniff, referrer policy)</li>
      <li>User directory listing restricted to administrators</li>
    </ul>
    <div className="marketing-hero-actions" style={{ marginTop: 8 }}>
    <Link to="/login" className="marketing-btn marketing-btn-primary">Sign in</Link>
    <Link to="/privacy" className="marketing-btn marketing-btn-secondary">Privacy policy</Link>
    </div>
    </div>
  </ProductShell>
);
